import { useMemo } from 'react';
import { lineStrings } from 'constants/strings';
import type { TRichMessageTemplate } from 'types/richMessage';

type Props = {
  template: TRichMessageTemplate | undefined;
  imageUrl: string | null;
  showOutlines: boolean;
};

const strings = lineStrings.management.richMessage;

export function RichMessageGridPreview({
  template,
  imageUrl,
  showOutlines,
}: Props) {
  const areaKeys = template?.areaKeys ?? [];
  const bounds = template?.bounds ?? [];

  const aspectStyle = useMemo(
    () =>
      template
        ? { aspectRatio: `${template.size.width} / ${template.size.height}` }
        : undefined,
    [template],
  );

  const cellClass =
    'flex items-center justify-center border-2 border-emerald-500/80 bg-emerald-500/10 text-xs font-bold text-emerald-800';

  const useFlowLayout = !template && !!imageUrl;

  return (
    <div
      className="relative w-full max-w-[320px] overflow-hidden rounded-lg border border-border/80 bg-surface"
      style={aspectStyle ?? (imageUrl ? { minHeight: 120 } : { minHeight: 80 })}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className={
            useFlowLayout
              ? 'block w-full object-contain'
              : 'absolute inset-0 h-full w-full object-cover'
          }
        />
      ) : (
        <div className="flex min-h-[80px] items-center justify-center p-4 text-center text-[10px] text-muted">
          {strings.imageHint}
        </div>
      )}
      {template && bounds.length > 0 ? (
        <div
          className={`pointer-events-none absolute inset-0 z-10 ${showOutlines ? 'opacity-100' : 'opacity-0'}`}
        >
          {bounds.map((b, i) => {
            const key = areaKeys[i] ?? String(i);
            const { width: W, height: H } = template.size;
            return (
              <div
                key={`${key}-${i}`}
                className={`absolute ${cellClass}`}
                style={{
                  left: `${(b.x / W) * 100}%`,
                  top: `${(b.y / H) * 100}%`,
                  width: `${(b.width / W) * 100}%`,
                  height: `${(b.height / H) * 100}%`,
                }}
              >
                {key}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
